import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2 } from 'lucide-react';
import {
    RoutineKind,
    RoutineLimits,
    RoutineSpec,
    RoutineTrigger,
    ScheduleFrequency,
    WEEKDAYS,
    describeTrigger,
} from '../servicesShared';
import { DirtySetter, SectionHeader } from './editorShared';

const KIND_OPTIONS: { value: RoutineKind; label: string; description: string }[] = [
    { value: 'job', label: 'Full wake', description: 'The smart model with full context (the heartbeat).' },
    { value: 'code', label: 'Script', description: 'A script the job wrote in its workspace; no model. Wakes the job when it finds something.' },
    { value: 'agent', label: 'Agent', description: 'A narrow prompt on a cheaper model; can wake the job.' },
];

const FREQUENCIES: { value: ScheduleFrequency; label: string }[] = [
    { value: 'interval', label: 'Every N minutes' },
    { value: 'hourly', label: 'Hourly' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
];

export const newRoutine = (): RoutineSpec => ({
    name: 'heartbeat',
    kind: 'job',
    trigger: { type: 'schedule', frequency: 'daily', at: '06:00' },
    wake_parent: 'always',
});

const TriggerEditor: React.FC<{ trigger: RoutineTrigger; onChange: (next: RoutineTrigger) => void }> = ({ trigger, onChange }) => {
    const type = trigger.type;
    return (
        <div className="grid sm:grid-cols-[150px_1fr] gap-2 items-start">
            <Select
                value={type}
                onValueChange={(value) =>
                    onChange(value === 'event' ? { type: 'event', name: '' } : { type: 'schedule', frequency: 'daily', at: '06:00' })
                }
            >
                <SelectTrigger>
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="schedule">On a schedule</SelectItem>
                    <SelectItem value="event">On an event</SelectItem>
                </SelectContent>
            </Select>
            {trigger.type === 'event' ? (
                <Input
                    className="font-mono text-sm"
                    placeholder="order.created"
                    value={trigger.name}
                    onChange={(event) => onChange({ type: 'event', name: event.target.value.trim() })}
                />
            ) : (
                <div className="flex flex-wrap gap-2">
                    <Select
                        value={trigger.frequency}
                        onValueChange={(value) => {
                            const frequency = value as ScheduleFrequency;
                            const next: RoutineTrigger = { type: 'schedule', frequency, timezone: trigger.timezone };
                            if (frequency === 'interval') next.interval_minutes = trigger.interval_minutes || 60;
                            else next.at = trigger.at || (frequency === 'hourly' ? '00:00' : '06:00');
                            if (frequency === 'weekly') next.weekday = trigger.weekday ?? 0;
                            if (frequency === 'monthly') next.day_of_month = trigger.day_of_month ?? 1;
                            onChange(next);
                        }}
                    >
                        <SelectTrigger className="w-[160px]">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {FREQUENCIES.map((item) => (
                                <SelectItem key={item.value} value={item.value}>
                                    {item.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {trigger.frequency === 'interval' ? (
                        <Input
                            type="number"
                            min={5}
                            className="w-[110px]"
                            value={trigger.interval_minutes ?? ''}
                            onChange={(event) => onChange({ ...trigger, interval_minutes: parseInt(event.target.value, 10) || 0 })}
                            placeholder="minutes"
                        />
                    ) : (
                        <Input
                            type="time"
                            className="w-[120px]"
                            value={trigger.at || ''}
                            onChange={(event) => onChange({ ...trigger, at: event.target.value })}
                            title={trigger.frequency === 'hourly' ? 'Minutes past the hour' : 'Time of day'}
                        />
                    )}
                    {trigger.frequency === 'weekly' && (
                        <Select value={String(trigger.weekday ?? 0)} onValueChange={(value) => onChange({ ...trigger, weekday: Number(value) })}>
                            <SelectTrigger className="w-[140px]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {WEEKDAYS.map((day, index) => (
                                    <SelectItem key={day} value={String(index)}>
                                        {day}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                    {trigger.frequency === 'monthly' && (
                        <Input
                            type="number"
                            min={1}
                            max={28}
                            className="w-[90px]"
                            value={trigger.day_of_month ?? 1}
                            onChange={(event) => onChange({ ...trigger, day_of_month: parseInt(event.target.value, 10) || 1 })}
                            title="Day of month (1–28)"
                        />
                    )}
                    {trigger.frequency !== 'interval' && trigger.frequency !== 'hourly' && (
                        <Input
                            className="w-[200px]"
                            placeholder="Timezone (default: the subject's)"
                            value={trigger.timezone || ''}
                            onChange={(event) => onChange({ ...trigger, timezone: event.target.value.trim() || undefined })}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

/** Default routines every job of this service starts with, and the limits on what a job may add. */
export const RoutinesTab: React.FC<{
    routines: RoutineSpec[];
    onRoutinesChange: DirtySetter<RoutineSpec[]>;
    limits: RoutineLimits;
    onLimitsChange: DirtySetter<RoutineLimits>;
}> = ({ routines, onRoutinesChange, limits, onLimitsChange }) => {
    const update = (index: number, patch: Partial<RoutineSpec>) =>
        onRoutinesChange((prev) => prev.map((existing, i) => (i === index ? { ...existing, ...patch } : existing)));

    return (
        <>
            <section className="mb-10">
                <SectionHeader
                    title="Default routines"
                    description="Copied into every job at assignment. The heartbeat is a full wake on a schedule; event routines react to the events the job subscribes to. Jobs can add their own within the limits below."
                />
                <div className="space-y-3">
                    {routines.map((routine, index) => (
                        <div key={index} className="rounded-lg border p-3 space-y-3">
                            <div className="flex items-center gap-2">
                                <Input
                                    className="font-mono text-sm w-[200px]"
                                    placeholder="heartbeat"
                                    value={routine.name}
                                    onChange={(event) => update(index, { name: event.target.value.toLowerCase().replace(/[^a-z0-9_-]+/g, '_').slice(0, 48) })}
                                />
                                <Select
                                    value={routine.kind}
                                    onValueChange={(value) => {
                                        const kind = value as RoutineKind;
                                        update(index, {
                                            kind,
                                            wake_parent: kind === 'job' ? 'always' : routine.wake_parent === 'always' ? 'on_request' : routine.wake_parent || 'on_request',
                                            ...(kind !== 'code' ? { script_path: undefined } : {}),
                                            ...(kind !== 'agent' ? { instructions: undefined, model: undefined } : {}),
                                        });
                                    }}
                                >
                                    <SelectTrigger className="w-[150px]">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {KIND_OPTIONS.map((item) => (
                                            <SelectItem key={item.value} value={item.value}>
                                                {item.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <span className="text-xs text-muted-foreground grow truncate">{describeTrigger(routine.trigger)}</span>
                                <Button variant="ghost" size="icon" onClick={() => onRoutinesChange((prev) => prev.filter((_, i) => i !== index))}>
                                    <Trash2 className="h-4 w-4 text-muted-foreground" />
                                </Button>
                            </div>
                            <p className="text-xs text-muted-foreground">{KIND_OPTIONS.find((item) => item.value === routine.kind)?.description}</p>
                            <TriggerEditor trigger={routine.trigger} onChange={(trigger) => update(index, { trigger })} />
                            {routine.kind === 'code' && (
                                <Input
                                    className="font-mono text-sm"
                                    placeholder="scripts/check_stock.ts (relative to the job workspace)"
                                    value={routine.script_path || ''}
                                    onChange={(event) => update(index, { script_path: event.target.value })}
                                />
                            )}
                            {routine.kind === 'agent' && (
                                <div className="grid sm:grid-cols-[1fr_200px] gap-2">
                                    <Textarea
                                        rows={3}
                                        placeholder="What to check and when to wake the job (at least 10 characters)…"
                                        value={routine.instructions || ''}
                                        onChange={(event) => update(index, { instructions: event.target.value })}
                                    />
                                    <Input
                                        placeholder="Model (optional)"
                                        value={routine.model || ''}
                                        onChange={(event) => update(index, { model: event.target.value.trim() || null })}
                                    />
                                </div>
                            )}
                            {routine.kind !== 'job' && (
                                <div className="flex flex-wrap items-center gap-4 text-sm">
                                    <div className="flex items-center gap-2">
                                        <Label className="text-xs text-muted-foreground">Wake the job</Label>
                                        <Select value={routine.wake_parent || 'on_request'} onValueChange={(value) => update(index, { wake_parent: value as RoutineSpec['wake_parent'] })}>
                                            <SelectTrigger className="w-[160px] h-8">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="on_request">When it asks</SelectItem>
                                                <SelectItem value="always">Every run</SelectItem>
                                                <SelectItem value="never">Never</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Label className="text-xs text-muted-foreground">Budget per run (credits)</Label>
                                        <Input
                                            type="number"
                                            min={0}
                                            step="any"
                                            className="w-[110px] h-8"
                                            value={routine.budget_per_run ?? ''}
                                            placeholder="default"
                                            onChange={(event) => update(index, { budget_per_run: event.target.value === '' ? null : Number(event.target.value) })}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={() => onRoutinesChange((prev) => [...prev, { ...newRoutine(), name: prev.length ? `routine_${prev.length + 1}` : 'heartbeat' }])}>
                        <Plus className="h-4 w-4 mr-1" /> Add routine
                    </Button>
                </div>
            </section>

            <section className="mb-10">
                <SectionHeader title="Routine limits" description="Guardrails on what a job may add for itself. Only a full wake can create or change routines; routines never create routines." />
                <div className="grid sm:grid-cols-2 gap-4 max-w-xl">
                    <div className="space-y-1">
                        <Label htmlFor="limit-max">Max routines per job</Label>
                        <Input
                            id="limit-max"
                            type="number"
                            min={1}
                            placeholder="6"
                            value={limits.max_per_job ?? ''}
                            onChange={(event) => onLimitsChange((prev) => ({ ...prev, max_per_job: event.target.value === '' ? undefined : parseInt(event.target.value, 10) }))}
                        />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="limit-interval">Minimum interval (minutes)</Label>
                        <Input
                            id="limit-interval"
                            type="number"
                            min={5}
                            placeholder="15"
                            value={limits.min_interval_minutes ?? ''}
                            onChange={(event) =>
                                onLimitsChange((prev) => ({ ...prev, min_interval_minutes: event.target.value === '' ? undefined : parseInt(event.target.value, 10) }))
                            }
                        />
                    </div>
                    <label className="flex items-center gap-2 text-sm sm:col-span-2">
                        <Switch checked={Boolean(limits.allow_agent_routines)} onCheckedChange={(checked) => onLimitsChange((prev) => ({ ...prev, allow_agent_routines: checked }))} />
                        <span className="flex flex-col">
                            <span>Jobs may add agent routines</span>
                            <span className="text-xs text-muted-foreground">A cheaper model on a timer that can wake the job. Default routines above may use them regardless.</span>
                        </span>
                    </label>
                    <div className="space-y-1 sm:col-span-2">
                        <Label htmlFor="limit-models">Allowed models for agent routines</Label>
                        <Input
                            id="limit-models"
                            placeholder="Comma-separated model names (blank = any)"
                            value={(limits.allowed_models || []).join(', ')}
                            onChange={(event) =>
                                onLimitsChange((prev) => ({
                                    ...prev,
                                    allowed_models: event.target.value.split(',').map((value) => value.trim()).filter(Boolean),
                                }))
                            }
                        />
                    </div>
                </div>
            </section>
        </>
    );
};

export default RoutinesTab;
